if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        mediaGallery: '[id^=Media-Gallery]',
        liveRegion: '[id^="GalleryStatus"]',
        viewer: '[id^="GalleryViewer"]',
        thumbnailsWrapper: '.f-product__media-thumbnails-wrapper',
        thumbnails: '[id^="GalleryThumbnails"]',
        thumbnailItems: ['[id^="GalleryThumbnailItem"]'],
        videos: ['.f-video__embed'],
        models: ['product-model'],
        medias: ['.f-product__media'],
        xrButton: '[data-first-xr-button]',
        sliderCounter: '.flickity-counter--current',
        toggleZoom: ['.js-photoswipe--zoom']
      };

      // Keep your original mobile scroll/drag helpers
      let touchingCarousel = false;
      let startXCoordinate;

      document.body.addEventListener('touchstart', (e) => {
        if (e.target.closest('.flickity-slider')) {
          touchingCarousel = true;
        } else {
          touchingCarousel = false;
          return;
        }
        startXCoordinate = e.touches[0].pageX;
      }, { passive: true });

      document.body.addEventListener(
        'touchmove',
        (e) => {
          if (touchingCarousel && e.cancelable) {
            if (Math.abs(e.touches[0].pageX - startXCoordinate) > 10) {
              e.preventDefault();
            }
          }
        },
        { passive: false }
      );
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      clearInterval(this.check);
    }

    init() {
      this.domNodes = queryDomNodes(this.selectors, this);
      this.mediaLayout = this.domNodes.mediaGallery.dataset.mediaLayout;
      this.onlyImage = this.dataset.onlyImage === 'true';
      this.sectionId = this.dataset.section;
      this.section = this.closest(`[data-section-id="${this.sectionId}"]`);
      this.enableZoom = this.dataset.enableZoom === 'true';
      this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === 'true';
      this.showFeaturedMedia = this.dataset.showFeaturedMedia === 'true';
      this.variantPickers = this.section.querySelector('[data-variant-picker]');
      this.disableDefaultVariant = this.variantPickers && this.variantPickers.dataset.disableDefaultVariant === 'true';
      this.variantData = this.getVariantData();
      const selectedVariantId = this.section && this.section.querySelector('input[name="id"]').value;
      this.currentVariant = this.variantData.find(variant => variant.id === Number(selectedVariantId));

      if (this.onlyImage) this.removeAttribute('data-media-loading');

      if (this.mediaLayout === 'carousel') {
        this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
        this.initGallerySlider();
      } else {
        const allMediaAnimate = this.domNodes.mediaGallery.querySelectorAll('.f-scroll-trigger');
        if (FoxThemeSettings.isMobile) {
          this.domNodes.mediaGallery && this.domNodes.mediaGallery.initSlider();
          this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
          if (allMediaAnimate.length > 0) {
            allMediaAnimate.forEach(media => {
              media.classList.remove('f-scroll-trigger');
            });
          }
        }
        document.addEventListener('matchMobile', () => {
          this.domNodes.mediaGallery && this.domNodes.mediaGallery.initSlider();
          this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
          if (allMediaAnimate.length > 0) {
            allMediaAnimate.forEach(media => {
              media.classList.remove('f-scroll-trigger');
            });
          }
        });
        document.addEventListener('unmatchMobile', () => {
          this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
          this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
          if (allMediaAnimate.length > 0) {
            allMediaAnimate.forEach(media => {
              media.classList.remove('f-scroll-trigger');
            });
          }
        });
        if (this.enableZoom) this.initImageZoom();
      }

      if (this.onlyImage && this.enableZoom) this.initImageZoom();

      if (!this.enableVariantGroupImages) {
        this.removeAttribute('data-media-loading');
      }
    }

    update() {
      if (this.domNodes.mediaGallery) this.domNodes.mediaGallery.update();
      if (this.domNodes.thumbnails) this.domNodes.thumbnails.update();
    }

    initGallerySlider() {
      if (!this.domNodes.medias.length) return;
      this.selectedSlide = null;
      this.selectedMediaType = '';

      this.check = setInterval(() => {
        this.mediaGallery = this.domNodes.mediaGallery.slider && this.domNodes.mediaGallery.slider.instance;
        this.thumbnails = this.domNodes.thumbnails && this.domNodes.thumbnails.slider && this.domNodes.thumbnails.slider.instance;

        if (this.mediaGallery && typeof this.mediaGallery == 'object') {
          clearInterval(this.check);

          // Bind change handlers
          this.mediaGallery.on('change', this.onSlideChanged.bind(this));

          // 🔹 Always keep drag enabled (desktop + mobile)
          // Also re-assert after every change just in case
          this.mediaGallery.on('change', () => {
            this.toggleDraggable(true);
            // Make sure swipe proxy is bound even if slides re-render
            this.bindVideoSwipeProxy();
          });

          // 🔹 Force gallery to start at second image (index 1)
          this.mediaGallery.select(1, true, false);
          if (this.thumbnails && this.thumbnails.cells && this.thumbnails.cells[1]) {
            this.thumbnails.select(1);
          }
          // (Removed: selecting thumb[0], which was canceling the advance)

          if (this.thumbnails) {
            this.handleScreenChange();
          }
          if (this.enableZoom) this.initImageZoom();

          if (this.currentVariant && !this.disableDefaultVariant && !this.showFeaturedMedia) {
            this.setActiveMedia(this.currentVariant.featured_media.id);
          }

          // 🔹 Enable starting a swipe directly on video/poster on touch devices
          this.bindVideoSwipeProxy();

          // Ensure draggable is on from the start
          this.toggleDraggable(true);
        }
      }, 100);
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }

    initImageZoom() {
      let dataSource = [];
      const allMedia = this.querySelectorAll('.f-product__media');
      if (allMedia) {
        allMedia.forEach(media => {
          if (media.dataset.mediaType === 'image') {
            dataSource.push({
              id: media.dataset.mediaIndex,
              src: media.dataset.src,
              width: media.dataset.pswpWidth,
              height: media.dataset.pswpHeight,
              mediaType: media.dataset.mediaType
            });
          }
          if (media.dataset.mediaType === 'model') {
            const html = `<div class="pswp__item--${media.dataset.mediaType}">${media.querySelector('product-model').outerHTML}</div>`;
            dataSource.push({
              id: media.dataset.mediaIndex,
              html: html,
              mediaType: media.dataset.mediaType
            });
          }
          if (media.dataset.mediaType === 'video' || media.dataset.mediaType == 'external_video') {
            const html = `<div class="pswp__item--${media.dataset.mediaType}">${media.querySelector('deferred-media').outerHTML}</div>`;
            dataSource.push({
              id: media.dataset.mediaIndex,
              html: html,
              mediaType: media.dataset.mediaType
            });
          }
        });
      }

      const options = {
        dataSource: dataSource,
        pswpModule: window.FoxTheme.PhotoSwipe,
        bgOpacity: 1,
        arrowPrev: false,
        arrowNext: false,
        zoom: false,
        close: false,
        counter: false,
        preloader: false
      };

      this.lightbox = new window.FoxTheme.PhotoSwipeLightbox(options);

      this.lightbox.addFilter('thumbEl', (thumbEl, data, index) => {
        const el = this.querySelector('[data-media-index="' + data.id + '"] img');
        if (el) return el;
        return thumbEl;
      });

      this.lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
        const el = this.querySelector('[data-media-index="' + slide.data.id + '"] img');
        if (el) return el.src;
        return placeholderSrc;
      });

      this.lightbox.on('change', () => {
        const currIndex = this.lightbox.pswp.currIndex;
        if (!this.onlyImage) {
          const slider = this.domNodes.mediaGallery.slider && this.domNodes.mediaGallery.slider.instance;
          if (slider) {
            setTimeout(() => {
              slider.select(currIndex, false, true);
            }, 300);
          }
        }
      });

      this.lightbox.on('pointerDown', (e) => {
        const currSlide = this.lightbox.pswp.currSlide.data;
        if (['video', 'model', 'external_video'].includes(currSlide.mediaType)) {
          e.preventDefault();
        }
      });

      this.lightbox.on('uiRegister', () => {
        if (!this.onlyImage) {
          this.lightbox.pswp.ui.registerElement({
            name: 'next',
            ariaLabel: 'Next slide',
            order: 3,
            isButton: true,
            html: '<svg class="pswp-icon-next" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>',
            onClick: () => this.lightbox.pswp.next()
          });
          this.lightbox.pswp.ui.registerElement({
            name: 'prev',
            ariaLabel: 'Previous slide',
            order: 1,
            isButton: true,
            html: '<svg class="pswp-icon-prev" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
            onClick: () => this.lightbox.pswp.prev()
          });
        }

        this.lightbox.pswp.ui.registerElement({
          name: 'close-zoom',
          ariaLabel: 'Close zoom image',
          order: 2,
          isButton: true,
          html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium  f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
          onClick: () => this.lightbox.pswp.close()
        });
      });

      this.lightbox.init();

      addEventDelegate({
        selector: this.selectors.toggleZoom[0],
        context: this,
        handler: (e, media) => {
          const isImage = media.dataset.mediaType === 'image';
          if (isImage) {
            const index = Number(media.dataset.mediaIndex) || 0;
            this.lightbox.loadAndOpen(index);
          }
        }
      });
    }

    onSlideChanged(index) {
      this.draggable = true; // track our intent
      this.selectedSlide = this.mediaGallery.selectedElement;
      this.selectedMediaType = this.selectedSlide.dataset.mediaType;
      this.selectedMediaId = this.selectedSlide.dataset.mediaId;

      const mediaAspectRatio = this.selectedSlide.dataset.aspectRatio;
      this.domNodes.viewer.style.setProperty('--media-aspect-ratio', mediaAspectRatio);

      // 🔹 Always keep dragging ON (desktop + mobile), even on video/model
      this.toggleDraggable(true);

      if (this.selectedMediaType === 'model') {
        this.domNodes.xrButton && this.domNodes.xrButton.setAttribute('data-shopify-model3d-id', this.selectedMediaId);
      }

      if (this.thumbnails) {
        this.thumbnails.select(index);
      }

      if (this.domNodes.sliderCounter) {
        this.domNodes.sliderCounter.textContent = index + 1;
      }

      this.domNodes.mediaGallery.classList.remove('pointer-move');

      this.playActiveMedia(this.selectedSlide);
    }

    handleScreenChange() {
      document.addEventListener('matchMobile', () => {
        this.domNodes.thumbnails.update();
        if (this.thumbnails.cells.length > 4) {
          this.thumbnails.options.wrapAround = true;
          this.domNodes.thumbnails.update();
        }
      });
      document.addEventListener('unmatchMobile', () => {
        this.domNodes.thumbnails.update();
        if (this.thumbnails.cells.length < 6) {
          this.thumbnails.options.wrapAround = false;
          this.domNodes.thumbnails.update();
        }
      });
      if (this.thumbnails.cells.length > 5) {
        this.domNodes.thumbnails.update();
        this.thumbnails.options.wrapAround = true;
        this.domNodes.thumbnails.update();
      }
    }

    // 🔹 Always-on drag
    toggleDraggable(status = false) {
      this.draggable = true;
      if (this.mediaGallery && this.mediaGallery.options) {
        this.mediaGallery.options.draggable = true;
        if (typeof this.mediaGallery.updateDraggable === 'function') {
          this.mediaGallery.updateDraggable();
        }
      }
    }

    // 🔹 Proxy horizontal swipes that start on the video/poster to Flickity
    bindVideoSwipeProxy() {
      const slider = this.domNodes.mediaGallery &&
                     this.domNodes.mediaGallery.slider &&
                     this.domNodes.mediaGallery.slider.instance;
      if (!slider || this.__videoSwipeBound) return;
      this.__videoSwipeBound = true;

      const root = this.domNodes.mediaGallery;
      const targets = root.querySelectorAll(
        'video, deferred-media, .deferred-media, .deferred-media__poster, .f-product__media--video, .f-product__media--video *'
      );

      targets.forEach(el => {
        if (el.dataset.vdragBound) return;
        el.dataset.vdragBound = '1';

        let sx = 0, sy = 0, dragging = false, did = false;

        const down = (e) => {
          const t = e.touches ? e.touches[0] : e;
          sx = t.clientX; sy = t.clientY;
          dragging = true; did = false;
          // prevent native element drag/select for a snappier swipe
          if (e.cancelable) e.preventDefault();
          window.addEventListener('touchmove', move, { passive: false });
          window.addEventListener('touchend', up, { once: true });
          window.addEventListener('pointermove', move, { passive: false });
          window.addEventListener('pointerup', up, { once: true });
        };

        const move = (e) => {
          if (!dragging) return;
          const t = e.touches ? e.touches[0] : e;
          const dx = t.clientX - sx;
          const dy = t.clientY - sy;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 6) {
            did = true;
            if (e.cancelable) e.preventDefault();
          }
        };

        const up = (e) => {
          if (!dragging) return;
          dragging = false;
          const t = e.changedTouches ? e.changedTouches[0] : e;
          const dx = t.clientX - sx;
          const dy = t.clientY - sy;
          const horizontal = Math.abs(dx) > Math.abs(dy);
          const THRESH = 40;

          if (did && horizontal && Math.abs(dx) > THRESH) {
            dx < 0 ? slider.next() : slider.previous();
          } else {
            // pass through as normal tap/click (play/pause)
            try { el.click(); } catch (_) {}
          }
        };

        el.addEventListener('touchstart', down, { passive: false });
        el.addEventListener('pointerdown', down, { passive: false });
      });
    }

    playActiveMedia(selected) {
      window.pauseAllMedia();
      const deferredMedia = selected.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    setActiveMedia(mediaId) {
      const selectedMedia = this.domNodes.mediaGallery.querySelector(`[data-media-id="${mediaId}"]`);
      if (!selectedMedia) return;
      const mediaIndex = Number(selectedMedia.dataset.mediaIndex);
      this.mediaGallery && this.mediaGallery.select(mediaIndex, true, false);

      this.scrollIntoView(selectedMedia);
      this.preventStickyHeader();
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    scrollIntoView(selectedMedia) {
      if (this.mediaLayout !== 'stacked' || FoxThemeSettings.isMobile) return;
      selectedMedia.scrollIntoView({
        behavior: 'smooth'
      });
    }
  });
}
